import React, { useEffect, useMemo } from 'react';
import { iconUrl, kindLabel, stepFor } from './assets';
import { fmtDistance, metres, nearestBus, nearestLine } from './geo';
import ProfileSection from './ProfileSection';

const ADD_ORDER = ['load', 'pv', 'wind', 'ev_charger', 'heat_pump', 'battery', 'bus', 'line', 'trafo', 'switch', 'ext_grid'];
const close = (a, b) => Math.abs(Number(a) - Number(b)) < 1e-6;

function TemplateSelect({ value, onChange, templates, describe }) {
  const net = templates.filter(t => t.source === 'net');
  const lib = templates.filter(t => t.source !== 'net');
  return (
    <select value={value || ''} onChange={(e) => onChange(e.target.value)}>
      {net.length > 0 && (
        <optgroup label="Used in this net">
          {net.map(t => <option key={t.name} value={t.name}>{describe ? describe(t) : t.name}</option>)}
        </optgroup>
      )}
      <optgroup label="pandapower library">
        {lib.map(t => <option key={t.name} value={t.name}>{describe ? describe(t) : t.name}</option>)}
      </optgroup>
    </select>
  );
}

// Fill in sensible defaults whenever the asset kind changes.
function withDefaults(draft, catalog, view) {
  const kind = draft.kind;
  const info = catalog.kinds.find(k => k.kind === kind);
  const d = { ...draft, init: kind };
  const near = (pred) => (d.pos ? nearestBus(view, d.pos, pred)?.bus : null);

  d.profile = { source: 'none' };
  if (info?.attach === 'bus') {
    d.params = Object.fromEntries(info.fields.map(f => [f.key, f.default]));
    if (d.bus == null) d.bus = near(b => b.in_service)?.id ?? null;
    d.connect = d.pos ? (d.connect || 'new_bus') : 'bus';
  } else if (kind === 'bus') {
    const nb = near(b => b.in_service);
    d.vn_kv = d.vn_kv ?? nb?.vn_kv ?? catalog.voltage_levels[0];
    d.connectBus = d.connectBus ?? true;
    if (d.bus == null) d.bus = nb?.id ?? null;
  } else if (kind === 'trafo') {
    const tpl = catalog.trafo_templates.find(t => t.source === 'net') || catalog.trafo_templates[0];
    d.trafo_template = d.trafo_template || tpl?.name;
    d.supply = d.supply || 'grid';
    d.lv_side = d.lv_side || 'new';
    d.in_service = d.in_service ?? true;
    const t = catalog.trafo_templates.find(x => x.name === d.trafo_template);
    if (d.hv_bus == null) {
      d.hv_bus = (d.bus != null && t && close(view.busById.get(d.bus)?.vn_kv, t.vn_hv_kv)) ? d.bus
        : near(b => t && close(b.vn_kv, t.vn_hv_kv))?.id ?? null;
    }
  } else if (kind === 'line') {
    if (d.from_bus == null) d.from_bus = d.bus ?? near(b => b.in_service)?.id ?? null;
  } else if (kind === 'switch') {
    if (d.element == null && d.pos) d.element = nearestLine(view, d.pos)?.line.id ?? null;
    const ln = view.byKey.get(`line:${d.element}`);
    if (ln && (d.bus == null || (d.bus !== ln.from_bus && d.bus !== ln.to_bus))) d.bus = ln.from_bus;
    d.closed = d.closed ?? true;
  }
  return d;
}

function BusRow({ label, busId, view, pos, onPick, picking }) {
  const bus = busId != null ? view.busById.get(busId) : null;
  return (
    <div className="nl-field">
      <span>{label}</span>
      <div className="nl-pickrow">
        <div className="nl-picked">
          {bus ? (
            <>
              <b>{bus.name}</b>
              <small>{bus.vn_kv} kV{pos ? ` · ${fmtDistance(metres(pos, bus.pos))} away` : ''}</small>
            </>
          ) : <small>none selected</small>}
        </div>
        <button type="button" className={`nl-btn small${picking ? ' active' : ''}`} onClick={onPick}>
          {picking ? 'Click a bus…' : 'Pick on map'}
        </button>
      </div>
    </div>
  );
}

export default function AddAssetForm({ sid, catalog, view, draft, setDraft, onSubmit, onCancel, startPick, pick, busy }) {
  const info = catalog.kinds.find(k => k.kind === draft.kind);

  useEffect(() => {
    if (draft.init !== draft.kind) setDraft(d => withDefaults(d, catalog, view));
  }, [draft.kind, draft.init, catalog, view, setDraft]);

  const set = (patch) => setDraft(d => ({ ...d, ...patch }));
  const setParam = (k, v) => setDraft(d => ({ ...d, params: { ...d.params, [k]: v } }));

  const pickBus = (field, pred, prompt) => startPick({
    field, filter: 'bus', prompt,
    accept: (o) => o.table === 'bus' && (!pred || pred(o)),
    onPick: (o) => set({ [field]: o.id }),
  });

  const lineTplFor = (busId) => {
    const vn = view.busById.get(busId)?.vn_kv;
    return catalog.default_line_template[String(Number(vn).toFixed(1))] ??
      Object.entries(catalog.default_line_template).find(([k]) => close(k, vn))?.[1];
  };

  const cableBus = info?.attach === 'bus' ? draft.bus : draft.kind === 'bus' ? draft.bus
    : draft.kind === 'line' ? draft.from_bus : null;
  const cableTpl = draft.line_template || (cableBus != null ? lineTplFor(cableBus) : undefined);
  const trafoTpl = catalog.trafo_templates.find(t => t.name === draft.trafo_template);
  const picking = (f) => pick?.field === f;

  const kindsAvailable = useMemo(
    () => ADD_ORDER.filter(k => catalog.kinds.some(c => c.kind === k)), [catalog]);

  const buildSpec = () => {
    const base = { kind: draft.kind, name: draft.name || undefined };
    if (info?.attach === 'bus') {
      return { ...base, bus: draft.bus, connect: draft.connect, pos: draft.pos,
        line_template: draft.connect === 'new_bus' ? cableTpl : undefined, params: draft.params,
        profile: draft.profile && draft.profile.source !== 'none' ? draft.profile : undefined };
    }
    if (draft.kind === 'bus') {
      return { ...base, pos: draft.pos, vn_kv: draft.vn_kv,
        bus: draft.connectBus ? draft.bus : null, line_template: draft.connectBus ? cableTpl : undefined };
    }
    if (draft.kind === 'trafo') {
      return { ...base, trafo_template: draft.trafo_template, supply: draft.supply, in_service: !!draft.in_service,
        hv_bus: draft.supply === 'grid' ? draft.hv_bus : undefined,
        lv_bus: draft.lv_side === 'existing' ? draft.lv_bus : undefined, pos: draft.pos };
    }
    if (draft.kind === 'line') {
      return { ...base, from_bus: draft.from_bus, to_bus: draft.to_bus, line_template: cableTpl,
        length_km: draft.length_km || undefined };
    }
    if (draft.kind === 'switch') return { ...base, et: 'l', element: draft.element, bus: draft.bus, closed: draft.closed };
    return base;
  };

  const ready = (() => {
    if (draft.init !== draft.kind) return false;
    if (info?.attach === 'bus') return draft.bus != null && (draft.connect !== 'new_bus' || draft.pos);
    if (draft.kind === 'bus') return draft.pos && (!draft.connectBus || draft.bus != null);
    if (draft.kind === 'trafo') {
      return !!draft.trafo_template
        && (draft.supply === 'ext_grid' ? (draft.pos || (draft.lv_side === 'existing' && draft.lv_bus != null)) : draft.hv_bus != null)
        && (draft.lv_side !== 'existing' || draft.lv_bus != null);
    }
    if (draft.kind === 'line') return draft.from_bus != null && draft.to_bus != null && draft.from_bus !== draft.to_bus;
    if (draft.kind === 'switch') return draft.element != null && draft.bus != null;
    return false;
  })();

  const lineSel = draft.kind === 'switch' ? view.byKey.get(`line:${draft.element}`) : null;
  const sameVoltageAs = (id) => (b) => close(b.vn_kv, view.busById.get(id)?.vn_kv);

  return (
    <div className="nl-card nl-add-card">
      <div className="nl-card-head">
        <img src={iconUrl(draft.kind)} alt="" className="nl-card-icon" />
        <div className="nl-card-title">
          <div className="nl-card-name">Add asset</div>
          <div className="nl-card-kind">
            {draft.pos ? `at ${draft.pos[1].toFixed(5)}, ${draft.pos[0].toFixed(5)}` : 'on the selected element'}
          </div>
        </div>
        <button className="nl-icon-btn" onClick={onCancel} aria-label="Cancel">×</button>
      </div>

      <div className="nl-card-body">
        <label className="nl-field">
          <span>Asset type</span>
          <select value={draft.kind}
            onChange={(e) => setDraft(d => ({ pos: d.pos, bus: d.bus, element: d.element, kind: e.target.value }))}>
            {kindsAvailable.map(k => <option key={k} value={k}>{kindLabel(k)}</option>)}
          </select>
        </label>

        {info?.attach === 'bus' && (
          <>
            {draft.pos && (
              <div className="nl-radio">
                <label>
                  <input type="radio" checked={draft.connect === 'new_bus'} onChange={() => set({ connect: 'new_bus' })} />
                  New bus here, cabled to a bus
                </label>
                <label>
                  <input type="radio" checked={draft.connect === 'bus'} onChange={() => set({ connect: 'bus' })} />
                  Directly on an existing bus
                </label>
              </div>
            )}
            <BusRow label={draft.connect === 'new_bus' ? 'Cable from' : 'Bus'} busId={draft.bus} view={view}
              pos={draft.pos} picking={picking('bus')}
              onPick={() => pickBus('bus', null, 'Click the bus to connect to')} />
            {draft.connect === 'new_bus' && (
              <label className="nl-field">
                <span>Cable type</span>
                <TemplateSelect value={cableTpl} onChange={(v) => set({ line_template: v })} templates={catalog.line_templates} />
              </label>
            )}
            <div className="nl-grid2">
              {info.fields.map(f => (
                <label key={f.key} className="nl-field">
                  <span>{f.key}{f.unit ? ` (${f.unit})` : ''}</span>
                  <input type="number" step={stepFor(f.key)} value={draft.params?.[f.key] ?? ''}
                    onChange={(e) => setParam(f.key, e.target.value)} />
                </label>
              ))}
            </div>
            {draft.kind !== 'ext_grid' && draft.init === draft.kind && (
              <ProfileSection sid={sid} kind={draft.kind} pMw={draft.params?.p_mw ?? draft.params?.max_p_mw}
                value={draft.profile} onChange={(profile) => set({ profile })} />
            )}
          </>
        )}

        {draft.kind === 'bus' && (
          <>
            <label className="nl-field">
              <span>Voltage level</span>
              <select value={draft.vn_kv ?? ''} onChange={(e) => {
                const vn = Number(e.target.value);
                const nb = draft.pos ? nearestBus(view, draft.pos, b => close(b.vn_kv, vn))?.bus : null;
                set({ vn_kv: vn, bus: nb?.id ?? null, line_template: undefined });
              }}>
                {catalog.voltage_levels.map(v => <option key={v} value={v}>{v} kV</option>)}
              </select>
            </label>
            <label className="nl-check">
              <input type="checkbox" checked={!!draft.connectBus} onChange={(e) => set({ connectBus: e.target.checked })} />
              Connect it with a cable
            </label>
            {draft.connectBus && (
              <>
                <BusRow label="Cable from" busId={draft.bus} view={view} pos={draft.pos} picking={picking('bus')}
                  onPick={() => pickBus('bus', b => close(b.vn_kv, draft.vn_kv), `Click a ${draft.vn_kv} kV bus`)} />
                <label className="nl-field">
                  <span>Cable type</span>
                  <TemplateSelect value={cableTpl} onChange={(v) => set({ line_template: v })} templates={catalog.line_templates} />
                </label>
              </>
            )}
          </>
        )}

        {draft.kind === 'trafo' && (
          <>
            <label className="nl-field">
              <span>Transformer type</span>
              <TemplateSelect value={draft.trafo_template}
                onChange={(v) => {
                  const t = catalog.trafo_templates.find(x => x.name === v);
                  const keep = draft.hv_bus != null && t && close(view.busById.get(draft.hv_bus)?.vn_kv, t.vn_hv_kv);
                  const nb = !keep && draft.pos ? nearestBus(view, draft.pos, b => t && close(b.vn_kv, t.vn_hv_kv))?.bus : null;
                  const keepLv = draft.lv_bus != null && t && close(view.busById.get(draft.lv_bus)?.vn_kv, t.vn_lv_kv);
                  set({ trafo_template: v, hv_bus: keep ? draft.hv_bus : nb?.id ?? null, lv_bus: keepLv ? draft.lv_bus : null });
                }}
                templates={catalog.trafo_templates} />
            </label>
            <span className="nl-field-label">Supply (HV side)</span>
            <div className="nl-radio">
              <label>
                <input type="radio" checked={draft.supply === 'grid'} onChange={() => set({ supply: 'grid' })} />
                From the existing grid (an HV bus of this net)
              </label>
              <label>
                <input type="radio" checked={draft.supply === 'ext_grid'} onChange={() => set({ supply: 'ext_grid' })} />
                Own external grid (new supply point)
              </label>
            </div>
            {draft.supply === 'grid' ? (
              <BusRow label={`HV bus${trafoTpl ? ` (${trafoTpl.vn_hv_kv} kV)` : ''}`} busId={draft.hv_bus} view={view}
                pos={draft.pos} picking={picking('hv_bus')}
                onPick={() => pickBus('hv_bus', b => !trafoTpl || close(b.vn_kv, trafoTpl.vn_hv_kv), 'Click the HV-side bus')} />
            ) : (
              <p className="nl-hint">
                A new {trafoTpl?.vn_hv_kv ?? ''} kV bus with an external grid (slack, 1.0 p.u.) is created
                {draft.pos ? ' here' : ' next to the LV bus'}. It feeds this transformer independently of the rest of the net.
              </p>
            )}
            <span className="nl-field-label">LV side</span>
            <div className="nl-radio">
              <label>
                <input type="radio" checked={draft.lv_side === 'new'} onChange={() => set({ lv_side: 'new' })} />
                New {trafoTpl?.vn_lv_kv ?? ''} kV bus
              </label>
              <label>
                <input type="radio" checked={draft.lv_side === 'existing'} onChange={() => set({ lv_side: 'existing' })} />
                An existing bus (e.g. a second or standby transformer)
              </label>
            </div>
            {draft.lv_side === 'existing' && (
              <BusRow label={`LV bus${trafoTpl ? ` (${trafoTpl.vn_lv_kv} kV)` : ''}`} busId={draft.lv_bus} view={view}
                pos={draft.pos} picking={picking('lv_bus')}
                onPick={() => pickBus('lv_bus', b => !trafoTpl || close(b.vn_kv, trafoTpl.vn_lv_kv), 'Click the LV-side bus')} />
            )}
            <label className="nl-check">
              <input type="checkbox" checked={!!draft.in_service} onChange={(e) => set({ in_service: e.target.checked })} />
              In operation
            </label>
            {!draft.in_service && (
              <p className="nl-hint">Added switched off: it carries no power until you set in_service on.
                {draft.lv_side === 'new' && ' Its new LV bus stays unsupplied until then.'}</p>
            )}
          </>
        )}

        {draft.kind === 'line' && (
          <>
            <BusRow label="From bus" busId={draft.from_bus} view={view} pos={draft.pos} picking={picking('from_bus')}
              onPick={() => pickBus('from_bus', null, 'Click the start bus')} />
            <BusRow label="To bus" busId={draft.to_bus} view={view} pos={draft.pos} picking={picking('to_bus')}
              onPick={() => pickBus('to_bus', draft.from_bus != null ? sameVoltageAs(draft.from_bus) : null,
                'Click the end bus (same voltage)')} />
            <label className="nl-field">
              <span>Cable type</span>
              <TemplateSelect value={cableTpl} onChange={(v) => set({ line_template: v })} templates={catalog.line_templates} />
            </label>
            <label className="nl-field">
              <span>Length (km) — empty = straight-line distance</span>
              <input type="number" step="any" value={draft.length_km ?? ''} onChange={(e) => set({ length_km: e.target.value })} />
            </label>
          </>
        )}

        {draft.kind === 'switch' && (
          <>
            <div className="nl-field">
              <span>On line</span>
              <div className="nl-pickrow">
                <div className="nl-picked">{lineSel ? <b>{lineSel.name}</b> : <small>none selected</small>}</div>
                <button type="button" className={`nl-btn small${picking('element') ? ' active' : ''}`}
                  onClick={() => startPick({
                    field: 'element', filter: 'line', prompt: 'Click the line for the switch',
                    accept: (o) => o.table === 'line',
                    onPick: (o) => set({ element: o.id, bus: o.from_bus }),
                  })}>
                  {picking('element') ? 'Click a line…' : 'Pick on map'}
                </button>
              </div>
            </div>
            {lineSel && (
              <label className="nl-field">
                <span>At the end near</span>
                <select value={draft.bus ?? ''} onChange={(e) => set({ bus: Number(e.target.value) })}>
                  {[lineSel.from_bus, lineSel.to_bus].map(b => (
                    <option key={b} value={b}>{view.busById.get(b)?.name || `Bus ${b}`}</option>
                  ))}
                </select>
              </label>
            )}
            <label className="nl-check">
              <input type="checkbox" checked={!!draft.closed} onChange={(e) => set({ closed: e.target.checked })} />
              Closed
            </label>
          </>
        )}

        <label className="nl-field">
          <span>Name (optional)</span>
          <input value={draft.name || ''} onChange={(e) => set({ name: e.target.value })} placeholder="auto" />
        </label>
      </div>

      <div className="nl-card-actions">
        <button className="nl-btn ghost" onClick={onCancel}>Cancel</button>
        <button className="nl-btn primary" disabled={!ready || busy} onClick={() => onSubmit(buildSpec())}>
          {busy ? 'Adding…' : `Add ${kindLabel(draft.kind)}`}
        </button>
      </div>
    </div>
  );
}
